import React, { useState, useEffect } from 'react';
import {
  Building, Shield,
  Plus, Edit, Download, X,
  ArrowUpDown, ChevronUp, ChevronDown
} from 'lucide-react';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { FilterButton } from '../../../../components/UI/FilterButton';
import { Pagination } from '../../../../components/UI/Pagination';
import TrainingProviderForm from '../modals/TrainingProviderForm';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import toast from '../../../../utils/toast';

const TrainingProvidersTab = ({ onStatsUpdate }) => {
  const [trainingProviders, setTrainingProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('all');
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal State
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);


  useEffect(() => {
    loadTrainingProviders();
  }, []);

  const loadTrainingProviders = async () => {
    try {
      setLoading(true);
      // Request large page size to get all providers (MAX_PAGE_SIZE is 50000)
      const response = await fetch('/api/v1/configuration/training-providers/?page_size=10000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to load providers');
      
      const data = await response.json();
      const providers = data.results || data;
      setTrainingProviders(providers);
    } catch (error) {
      console.error('Error loading training providers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter providers
  const filteredProviders = trainingProviders.filter(provider => {
    const matchesSearch = 
      provider.name?.toLowerCase().includes(providerSearchTerm.toLowerCase()) ||
      provider.uen?.toLowerCase().includes(providerSearchTerm.toLowerCase()) ||
      provider.contact_email?.toLowerCase().includes(providerSearchTerm.toLowerCase()) ||
      provider.trading_name?.toLowerCase().includes(providerSearchTerm.toLowerCase());
    
    const matchesType = selectedProviderType === 'all' || provider.provider_type === selectedProviderType;
    const matchesStatus = selectedVerificationStatus === 'all' || provider.uen_verification_status === selectedVerificationStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 ml-1 text-red-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-red-600" />;
  };

  // Sort providers
  const sortedProviders = [...filteredProviders].sort((a, b) => {
    const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
    
    switch (sortConfig.key) {
      case 'name':
        return multiplier * (a.name || '').localeCompare(b.name || '');
      case 'type':
        return multiplier * (a.provider_type || '').localeCompare(b.provider_type || '');
      case 'status':
        return multiplier * (a.uen_verification_status || '').localeCompare(b.uen_verification_status || '');
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedProviders.length / itemsPerPage);
  const paginatedProviders = sortedProviders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get provider type label
  const getProviderTypeLabel = (type) => {
    const labels = {
      'private': 'Private',
      'public': 'Public',
      'government': 'Government',
      'industry': 'Industry'
    };
    return labels[type] || type || 'Unknown';
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [providerSearchTerm, selectedProviderType, selectedVerificationStatus]);

  const hasActiveFilters = providerSearchTerm || selectedProviderType !== 'all' || selectedVerificationStatus !== 'all';

  const clearFilters = () => {
    setProviderSearchTerm('');
    setSelectedProviderType('all');
    setSelectedVerificationStatus('all');
  };

  const handleExport = () => {
    toast.success('✅ Provider list exported to Excel');
  };

  const handleVerifyUEN = async (uen) => {
    try {
      const response = await fetch(`/api/v1/configuration/uen-verification/verify/${uen}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        await loadTrainingProviders();
        onStatsUpdate && onStatsUpdate();
      }
    } catch (error) {
      console.error('Error verifying UEN:', error);
    }
  };

  const handleProviderSave = async (provider) => {
    await loadTrainingProviders();
    onStatsUpdate && onStatsUpdate();
    setShowProviderForm(false);
    setEditingProvider(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Provider Form Modal */}
      <TrainingProviderForm
        provider={editingProvider}
        isOpen={showProviderForm}
        onClose={() => {
          setShowProviderForm(false);
          setEditingProvider(null);
        }}
        onSave={handleProviderSave}
      />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate min-w-0">Training Provider Directory</h2>
        <button 
          onClick={() => {
            setEditingProvider(null);
            setShowProviderForm(true);
          }}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0"
          aria-label="Add Provider"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Provider</span>
        </button>
      </div>

      {/* Search and Toolbar */}
      <div className="card">
        <div className="card-content">
          {/* Main Toolbar Row */}
          <div className="flex items-center justify-between gap-4">
            {/* Search */}
            <DebouncedSearchInput
              value={providerSearchTerm}
              onSearch={setProviderSearchTerm}
              placeholder="Search by name, UEN, or contact..."
              className="max-w-xl"
            />
            
            {/* Actions - Icon-only buttons */}
            <div className="flex items-center gap-2">
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={(providerSearchTerm ? 1 : 0) + (selectedProviderType !== 'all' ? 1 : 0) + (selectedVerificationStatus !== 'all' ? 1 : 0)}
                onClick={() => setShowFilters(!showFilters)}
              />
              <button
                onClick={handleExport}
                className="icon-button-admin"
                disabled={sortedProviders.length === 0}
                title="Export to Excel"
                aria-label="Export to Excel"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Expandable Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Provider Type</label>
                  <select
                    value={selectedProviderType}
                    onChange={(e) => setSelectedProviderType(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Provider Types</option>
                    <option value="private">Private Training Provider</option>
                    <option value="public">Public Institution</option>
                    <option value="government">Government Agency</option>
                    <option value="industry">Industry Association</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Verification Status</label>
                  <select
                    value={selectedVerificationStatus}
                    onChange={(e) => setSelectedVerificationStatus(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Status</option>
                    <option value="verified">UEN Verified</option>
                    <option value="pending">Pending Verification</option>
                    <option value="failed">Verification Failed</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {providerSearchTerm && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Search: "{providerSearchTerm}"
                  <button onClick={() => setProviderSearchTerm('')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedProviderType !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Type: {selectedProviderType}
                  <button onClick={() => setSelectedProviderType('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedVerificationStatus !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Status: {selectedVerificationStatus}
                  <button onClick={() => setSelectedVerificationStatus('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button onClick={clearFilters} className="text-xs text-red-600 hover:underline ml-2">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 text-sm text-gray-500">
        <span>Showing {paginatedProviders.length} of {sortedProviders.length} providers</span>
        {totalPages > 1 && (
          <span>Page {currentPage} of {totalPages}</span>
        )}
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-3">
        {paginatedProviders.map((provider) => (
          <div key={provider.uen} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{provider.name}</div>
                <div className="text-xs text-gray-500 font-mono truncate">{provider.uen}</div>
              </div>
              <div className="flex-shrink-0">
                <StatusBadge
                  status={
                    provider.uen_verification_status === 'verified' ? 'verified' :
                    provider.uen_verification_status === 'pending' ? 'pending' :
                    'failed'
                  }
                  label={
                    provider.uen_verification_status === 'verified' ? 'Verified' :
                    provider.uen_verification_status === 'pending' ? 'Pending' :
                    provider.uen_verification_status === 'failed' ? 'Failed' : 'Pending'
                  }
                  showIcon
                />
              </div>
            </div>
            <div className="mt-3 space-y-2 flex-1">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Type</span>
                <span className="text-right min-w-0">
                  <StatusBadge
                    status="draft"
                    label={getProviderTypeLabel(provider.provider_type)}
                  />
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Contact</span>
                <span className="text-gray-900 text-right truncate min-w-0">
                  {provider.contact_email || '—'}
                </span>
              </div>
            </div>
            <div className="card-action-footer justify-end gap-2">
              <button
                onClick={() => {
                  setEditingProvider(provider);
                  setShowProviderForm(true);
                }}
                className="icon-button-admin"
                title="Edit Provider"
                aria-label="Edit Provider"
              >
                <Edit className="w-4 h-4" />
              </button>
              {provider.uen_verification_status !== 'verified' && (
                <button
                  onClick={() => handleVerifyUEN(provider.uen)}
                  className="icon-button-admin"
                  title="Verify UEN"
                  aria-label="Verify UEN"
                >
                  <Shield className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Provider Table */}
      <div className="card hidden md:block">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('name')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Provider
                    {getSortIcon('name')}
                  </button>
                </th>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('type')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Type
                    {getSortIcon('type')}
                  </button>
                </th>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('status')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Status
                    {getSortIcon('status')}
                  </button>
                </th>
                <th className="table-header-cell">Contact</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {paginatedProviders.map((provider) => (
                <tr key={provider.uen} className="table-row">
                  {/* Provider Column - Name + UEN below */}
                  <td className="table-cell">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {provider.name}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {provider.uen}
                      </div>
                    </div>
                  </td>
                  {/* Type Column */}
                  <td className="table-cell">
                    <StatusBadge
                      status="draft"
                      label={getProviderTypeLabel(provider.provider_type)}
                    />
                  </td>
                  {/* Status Column */}
                  <td className="table-cell">
                    <StatusBadge
                      status={
                        provider.uen_verification_status === 'verified' ? 'verified' :
                        provider.uen_verification_status === 'pending' ? 'pending' :
                        'failed'
                      }
                      label={
                        provider.uen_verification_status === 'verified' ? 'Verified' :
                        provider.uen_verification_status === 'pending' ? 'Pending' :
                        provider.uen_verification_status === 'failed' ? 'Failed' : 'Pending'
                      }
                      showIcon
                    />
                  </td>
                  {/* Contact Column */}
                  <td className="table-cell">
                    {provider.contact_email ? (
                      <span className="text-sm text-gray-600 truncate max-w-[200px] block" title={provider.contact_email}>
                        {provider.contact_email}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  {/* Actions Column - Icon-only buttons */}
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingProvider(provider);
                          setShowProviderForm(true);
                        }}
                        className="icon-button-admin"
                        title="Edit Provider"
                        aria-label="Edit Provider"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {provider.uen_verification_status !== 'verified' && (
                        <button
                          onClick={() => handleVerifyUEN(provider.uen)}
                          className="icon-button-admin"
                          title="Verify UEN"
                          aria-label="Verify UEN"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalCount={sortedProviders.length}
        pageSize={itemsPerPage}
        theme="admin"
        noun="providers"
      />

      {/* Empty State */}
      {filteredProviders.length === 0 && (
        <div className="card">
          <div className="card-content">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-red-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {hasActiveFilters ? 'No Matching Providers' : 'No Training Providers Found'}
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                {hasActiveFilters 
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first training provider.'}
              </p>
              {hasActiveFilters ? (
                <button onClick={clearFilters} className="btn btn-admin-outline">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setEditingProvider(null);
                    setShowProviderForm(true);
                  }}
                  className="btn btn-admin-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Provider
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingProvidersTab;
